/**
 * 93. Form / View Designer — layout; sections; tabs; conditional logic;
 * list/kanban/calendar views. Reads view definitions for custom entities.
 *
 * Arabic and English (C7): every word from the catalogs; the tabs are chosen
 * by id, never by their words; a view's and a layout's type are words, never
 * the stored value.
 *
 * Read only. Its "New view" button had no handler and was removed
 * (UI-HONEST): there is no screen designer before the launch
 * (configurability.md section 4.10), and a view definition saved today is
 * read by no module (its P1), so creating one would change nothing a person
 * sees.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Badge,
  DataTable,
  Tabs,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { valueLabel } from "@/lib/i18n/records";

interface ViewDefinition {
  id: string;
  entityDefinitionId: string;
  name: string;
  viewType: "List" | "Kanban" | "Calendar" | "Pivot" | "Tree" | "Map";
  isDefault: boolean;
  status: "Draft" | "Active" | "Superseded";
}

interface LayoutDefinition {
  id: string;
  name: string;
  layoutType: "Form" | "Wizard" | "Compact";
  status: "Draft" | "Active";
}

type Tab = "views" | "layouts";
const TABS: Tab[] = ["views", "layouts"];

export function FormViewDesignerPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>("views");
  const { data: views, isLoading: viewsLoading } = useApiList<ViewDefinition>(
    ["config", "views"],
    "/config/views",
  );
  const { data: layouts, isLoading: layoutsLoading } =
    useApiList<LayoutDefinition>(["config", "layouts"], "/config/layouts");

  return (
    <div>
      <PageHeader
        title={t("admin.formDesigner.title")}
        subtitle={t("admin.formDesigner.subtitle")}
      />
      <Tabs
        tabs={TABS.map((id) => ({
          id,
          label: t(`admin.formDesigner.tabs.${id}`),
        }))}
        active={tab}
        onChange={setTab}
      />

      {tab === "views" && (
        <Card title={t("admin.formDesigner.viewsTitle")}>
          {viewsLoading || !views ? (
            <Skeleton rows={5} />
          ) : (
            <DataTable<ViewDefinition>
              rows={views.items}
              columns={[
                { key: "name", label: t("admin.formDesigner.view") },
                {
                  key: "viewType",
                  label: t("fields.type"),
                  render: (r) => (
                    <Badge tone="primary">
                      {valueLabel(t, "viewType", r.viewType)}
                    </Badge>
                  ),
                },
                {
                  key: "isDefault",
                  label: t("admin.formDesigner.default"),
                  render: (r) =>
                    r.isDefault ? (
                      <Badge tone="success">{t("shared.yes")}</Badge>
                    ) : (
                      "—"
                    ),
                },
                {
                  key: "status",
                  label: t("fields.status"),
                  render: (r) => (
                    <StatusBadge status={r.status} type="ViewDefinition" />
                  ),
                },
              ]}
            />
          )}
        </Card>
      )}

      {tab === "layouts" && (
        <Card title={t("admin.formDesigner.layoutsTitle")}>
          {layoutsLoading || !layouts ? (
            <Skeleton rows={5} />
          ) : (
            <DataTable<LayoutDefinition>
              rows={layouts.items}
              columns={[
                { key: "name", label: t("admin.formDesigner.layout") },
                {
                  key: "layoutType",
                  label: t("fields.type"),
                  render: (r) => (
                    <Badge tone="default">
                      {valueLabel(t, "layoutType", r.layoutType)}
                    </Badge>
                  ),
                },
                {
                  key: "status",
                  label: t("fields.status"),
                  render: (r) => (
                    <StatusBadge status={r.status} type="LayoutDefinition" />
                  ),
                },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
}
